/**
 * A CAM file's tool name, resolved to a bit in the catalog.
 *
 * The design file names the tool in the designer's own words — "1/4" The Jenny
 * Compression PN:8675309-J", "V-Bit (60.0° - 1/4\")". Three readings of that
 * name are tried, strongest first:
 *
 * 1. A part number the catalog also states. That names the exact product, so
 *    the name's diameter (maybe rounded, maybe in mm) cannot veto it; the
 *    geometry only breaks a tie between catalog bits sharing the number.
 * 2. The geometry `classifyTool` reads (type, diameter, V-angle), when exactly
 *    one catalog bit has it.
 * 3. Whole-name similarity among the bits whose geometry agrees, when one name
 *    stands clearly above the rest.
 *
 * Only a single survivor is taken; anything else would be a guess written into
 * the user's project. "60-DEGREE V" states an angle and no diameter, and the
 * catalog stocks that angle in several sizes. "End Mill (1/4\")" states no cut
 * direction, so it is matched across every end-mill bitType and resolves only
 * where the catalog holds one of them at that diameter.
 */
import { IBitPublic, IBitBrandRef } from '@/redux/bit/bit.d';
import { IBitType, IBitUsedUnit } from '@/redux/post/post.d';
import { BitFamily, classifyTool } from '@/workers/camStrings';
import { nameSimilarity } from './textMatch';

const FAMILY_TYPES: Record<BitFamily, string[]> = {
  endmill: [
    IBitType.UPCUT_ENDMILL,
    IBitType.DOWNCUT_ENDMILL,
    IBitType.COMPRESSION_BIT,
  ],
};

/** Above the 0.5 a bare "End Mill (1/4\")" scores against "RIPPER 1/4\"
 * Roughing End Mill": generic words alone must never carry a match. */
const MIN_SIMILARITY = 0.6;
/** A runner-up this close means the name does not single out one bit. */
const MIN_LEAD = 0.15;

export type BitMatch = { bit: IBitPublic; brand?: IBitBrandRef };

const matchOf = (bit: IBitPublic): BitMatch => ({ bit, brand: bit.brands[0] });

/** Diameters are compared in inches; `classifyTool` reads inches out of a name,
 * so a catalog bit measured in mm is left for the user. */
function sameDiameter(bit: IBitPublic, dia: number | null): boolean {
  if (dia === null) return true;
  if (bit.toolUsed == null) return true;
  if (bit.toolUnit === IBitUsedUnit.MM) return false;
  return Math.abs(bit.toolUsed - dia) < 1e-6;
}

const normalisePn = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The part numbers a name states, normalised so "PN: 8675309-V60°" and
 * "pn8675309 v60" agree. Designers sometimes write the suffix after a space
 * ("PN: 8675309 SLIM"), so the next word joined on is a reading too. */
function partNumbers(name: string): Set<string> {
  const out = new Set<string>();
  [...name.matchAll(/\bpn(?![a-z])[\s:#]*(\S+)(?:\s+(\S+))?/gi)].forEach(
    ([, pn, next]) => {
      out.add(normalisePn(pn));
      if (next) out.add(normalisePn(pn + next));
    }
  );
  out.delete('');
  return out;
}

/** The pool's bit whose name reads most like the tool's, or null when none
 * reads close enough or two read about as close. */
function mostSimilar(toolName: string, pool: IBitPublic[]): BitMatch | null {
  const ranked = pool
    .map((bit) => ({ bit, s: nameSimilarity(toolName, bit.name) }))
    .sort((a, b) => b.s - a.s);
  const [best, next] = ranked;
  if (!best || best.s < MIN_SIMILARITY) return null;
  if (next && best.s - next.s < MIN_LEAD) return null;
  return matchOf(best.bit);
}

type Geometry = {
  bitType: string | null;
  dia: number | null;
  vAngle: number | null;
  bitFamily?: BitFamily;
};

function fitsGeometry(g: Geometry): (b: IBitPublic) => boolean {
  let types: string[] = [];
  if (g.bitType) types = [g.bitType];
  else if (g.bitFamily) types = FAMILY_TYPES[g.bitFamily];
  return (b) =>
    (types.length === 0 || types.includes(b.bitType ?? '')) &&
    sameDiameter(b, g.dia) &&
    // A V-bit's angle is the whole point of it; an angle that disagrees is a
    // different tool, and a catalog bit with none recorded cannot be checked.
    (g.vAngle === null || b.vBitDegree === g.vAngle);
}

/** The one catalog bit with this geometry, for a tool known by its numbers
 * rather than a name. A type and a diameter are both required. */
function matchCatalogGeometry(
  g: Geometry,
  bits: IBitPublic[]
): BitMatch | null {
  if (!(g.bitType || g.bitFamily) || g.dia === null) return null;
  const pool = bits.filter(fitsGeometry(g));
  return pool.length === 1 ? matchOf(pool[0]) : null;
}

/** The one bit with this geometry, else the one unbranded bit among those
 * that fit: a tool named only by its shape is the catalog's generic bit. */
export function matchGenericBit(
  g: Geometry,
  bits: IBitPublic[]
): BitMatch | null {
  const exact = matchCatalogGeometry(g, bits);
  if (exact || !(g.bitType || g.bitFamily) || g.dia === null) return exact;
  const fits = fitsGeometry(g);
  const generic = bits.filter((b) => fits(b) && partNumbers(b.name).size === 0);
  return generic.length === 1 ? matchOf(generic[0]) : null;
}

export function matchCatalogBit(
  toolName: string | null,
  bits: IBitPublic[]
): BitMatch | null {
  if (!toolName) return null;
  const g = classifyTool(toolName);
  const typed = Boolean(g.bitType || g.bitFamily);
  const fits = fitsGeometry(g);

  const stated = partNumbers(toolName);
  const samePn = (b: IBitPublic): boolean =>
    [...partNumbers(b.name)].some((pn) => stated.has(pn));
  const byPn = bits.filter(samePn);
  const sole = byPn.length > 1 ? byPn.filter(fits) : byPn;
  if (sole.length === 1) return matchOf(sole[0]);

  // A different part number is a different product, however alike the rest of
  // the two names reads.
  const pool = bits.filter(
    (b) =>
      fits(b) &&
      (stated.size === 0 || partNumbers(b.name).size === 0 || samePn(b))
  );
  if (typed && pool.length === 1) return matchOf(pool[0]);
  return mostSimilar(toolName, pool);
}
